/**
 * 上传前压缩的目标尺寸计算（纯函数，不依赖 DOM，方便单独测试）
 *
 * App 端 pp-front-uniapp/utils/imageResize.js 是同一份规则，两边改动要保持一致。
 *
 * ============ 为什么不能再「统一按最长边限制」============
 * 原来的规则是"最长边不超过 1920"。对普通照片没问题，但长图的最长边是高度，
 * 按它缩等于把宽度压成细条：线上帖子 2076953573215825921 的配图，EXIF 里记着
 * 原图是 1080x3626，上传后只剩 572x1920 —— 宽度少了将近一半，点开大图按屏幕宽度
 * 铺满时被放大近 2 倍，表格里的字基本认不清。图越长，宽度被压得越狠。
 *
 * ============ 现在的规则（参照微信朋友圈 / Luban 的通用做法）============
 * 1. 普通图（长边 : 短边 ≤ 2）：长边不超过 maxDimension（1920），和原来一样；
 * 2. 长图（长边 : 短边 > 2）：改为限制「短边」不超过 1280（也不超过 maxDimension），
 *    长边等比放开 —— 手机截图宽 1080~1290，基本能按原宽度保留，字不会糊；
 *    单屏截图（约 1080x2400，比例 2.2）也落在这一档，不再被缩成 864x1920；
 * 3. 兜底：总像素不超过 4096x4096（≈1677 万），长边不超过 30000；
 *    - 1677 万是 iOS Safari 单个 canvas 的面积上限，超过后 canvas 会直接画出空白图；
 *    - 30000 是数据万象处理原图的宽高上限，超过后缩略图会生成失败；
 *    - 只有极长的图（1080 宽时约 15500 以上）才会碰到兜底，此时宽度会按比例变窄，属于刻意的取舍；
 * 4. 只缩不放：原图已经满足条件就不改尺寸。
 */

// 长边 : 短边 超过这个比例按长图处理（微信 / Luban 的分界线）
export const LONG_IMAGE_RATIO = 2
// 长图的短边上限
export const LONG_IMAGE_MAX_SHORT_SIDE = 1280
// 总像素上限（4096²）
export const MAX_PIXELS = 4096 * 4096
// 长边上限
export const MAX_LONG_SIDE = 30000

// 调用方没传有效 maxDimension 时的默认值，与 cosUpload.js 的 compressImage 默认值一致
const FALLBACK_MAX_DIMENSION = 1920
// 浮点误差修正：1920/4000*4000 可能算成 1919.9999999999998，直接向下取整会少 1px
const EPSILON = 1e-6

/**
 * 计算压缩目标尺寸
 *
 * @param {number} width 原图宽
 * @param {number} height 原图高
 * @param {number} [maxDimension=1920] 普通图的长边上限；长图的短边上限也不会超过它
 * @returns {{width:number,height:number,resized:boolean,isLongImage:boolean}|null}
 *          宽高无效时返回 null；resized=false 表示原尺寸已满足条件，只需要质量压缩
 */
export function computeCompressSize(width, height, maxDimension) {
  const w = Number(width)
  const h = Number(height)
  if (!(w > 0) || !(h > 0)) return null

  const limit = Number(maxDimension) > 0 ? Number(maxDimension) : FALLBACK_MAX_DIMENSION
  const longSide = Math.max(w, h)
  const shortSide = Math.min(w, h)
  const isLongImage = longSide / shortSide > LONG_IMAGE_RATIO

  // 主规则：普通图限长边，长图限短边
  let scale = isLongImage
    ? Math.min(LONG_IMAGE_MAX_SHORT_SIDE, limit) / shortSide
    : limit / longSide
  // 兜底：总像素、长边
  scale = Math.min(scale, Math.sqrt(MAX_PIXELS / (w * h)), MAX_LONG_SIDE / longSide)

  if (scale >= 1) {
    return { width: w, height: h, resized: false, isLongImage }
  }
  // 向下取整：保证结果不会因为四舍五入越过总像素上限（canvas 超限会画出空白图）
  return {
    width: Math.max(1, Math.floor(w * scale + EPSILON)),
    height: Math.max(1, Math.floor(h * scale + EPSILON)),
    resized: true,
    isLongImage
  }
}

export default {
  LONG_IMAGE_RATIO,
  LONG_IMAGE_MAX_SHORT_SIDE,
  MAX_PIXELS,
  MAX_LONG_SIDE,
  computeCompressSize
}
